import React, {useCallback, useMemo } from "react";
import { LodgingTagsTranslations } from "./translations/LodgingTagsTranslations";
import {LodgingPresentationQuery} from "./operations.generated";

import "./LodgingTags.scss";

type LodgingPresentation = LodgingPresentationQuery["lodgingPresentation"]; 
type BookingOption = LodgingPresentation["selectedBookingOption"];

function LodgingTag({
    label,
    subType
} : {
    label: string;
    subType: string;
}) {
    return (
        <div className={`bwp-lodging-tag bwp-lodging-tag--${subType}`}>{label}</div>
    )
}

export default function LodgingTags({
    lodgingPresentation,
    showItems,
    itemsToShow,
    maxItemsToShow,
    showCleaningIncluded,
    showDiscount,
    translations,
    tagsOrder
} : {
    lodgingPresentation: LodgingPresentation;
    showItems: boolean;
    itemsToShow?: number[];
    maxItemsToShow?: number;
    showCleaningIncluded?: boolean;
    showDiscount?: boolean;
    translations: LodgingTagsTranslations,
    tagsOrder?: ("cleaning" | "discount" | "items")[];
}) {
    
    const bookingOption = lodgingPresentation?.selectedBookingOption;

    const itemIsShown = useCallback((itemPrice: BookingOption["itemPrices"][number]) => {
        if (!itemsToShow?.length) {
            return false;
        }
        return itemsToShow.findIndex(itemId => itemPrice.item.id === itemId) > -1;
    }, [itemsToShow]);
    
    if (!bookingOption) {
        return null;
    }

    const items = useMemo(() => {
        if (!showItems || !bookingOption || !bookingOption.itemPrices?.length) {
            return [];
        }
        
        return bookingOption.itemPrices
            .filter(ip => (
                ip.isMandatory &&
                itemIsShown(ip)
            ))
    }, [bookingOption?.itemPrices, itemsToShow, showItems]);

    const tags = useMemo(() => {
        const maxItems = maxItemsToShow || 3;
        const result = items
            .filter((ip, index) => index < maxItems)
            .map(ip => ({
                key: `tag-item-${ip.item.id}`,
                label: ip.item.name,
                subType: "item",
                sortIndex: tagsOrder?.length
                    ? tagsOrder.findIndex(t => t === "items") >= 0
                        ? tagsOrder.findIndex(t => t === "items")
                        : maxItems + 100
                    : maxItems + 100
            }));

        if (showCleaningIncluded && bookingOption?.cleaningIncludedInPriceWithMandatoryItems) {
            result.push({
                key: `tag-cleaning`,
                label: translations.cleaningIncludedLabel,
                subType: "cleaning",
                sortIndex: tagsOrder?.length
                    ? tagsOrder.findIndex(t => t === "cleaning") >= 0 
                        ? tagsOrder.findIndex(t => t === "cleaning")
                        : maxItems + 100
                    : maxItems + 100
            });
        }

        if (showDiscount && bookingOption.hasDiscount) {
            result.push({
                key: `tag-discount`,
                label: translations.discountLabel,
                subType: "discount",
                sortIndex: tagsOrder?.length
                    ? tagsOrder.findIndex(t => t === "discount") >= 0
                        ? tagsOrder.findIndex(t => t === "discount")
                        : maxItems + 100
                    : maxItems + 100
            });
        }
        
        result.sort((a, b) => {
            return a.sortIndex - b.sortIndex;
        });

        return result;
    }, [items, maxItemsToShow, tagsOrder, showDiscount, showCleaningIncluded, bookingOption?.hasDiscount, bookingOption?.cleaningIncludedInPriceWithMandatoryItems, translations]);
    
    return (
        <div className="bwp-lodging-tags">
            {tags.map(tag => <LodgingTag key={tag.key} label={tag.label} subType={tag.subType} />)}
        </div>
    );
}